import { defineComponent } from 'vue';
import Spinner from '~/components/Spinner.vue';
import Checkbox from '~/components/Checkbox.vue';
import { tradeAreaClient, } from '~/lib/client';
import { numWithDots } from '~/lib/utils';
import { store } from '~/store';
export default defineComponent({
    name: 'Catalog',
    components: {
        Spinner,
        Checkbox,
    },
    props: {
        brandLock: {
            type: String,
            default: null,
        },
        categoryLock: {
            type: String,
            default: null,
        },
        ecommerce: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['product-select'],
    data() {
        return {
            loading: false,
            loadingLoadMore: false,
            loadMoreEnabled: true,
            products: [],
            brands: [],
            brandMap: new Map([]),
            categories: [],
            childCategories: [],
            productPage: 0,
            selectedParentCategory: null,
            selectedChildCategories: [],
            selectedBrands: [],
            searchString: '',
        };
    },
    beforeMount() {
        this.init(false);
    },
    methods: {
        async init(resetScroll = true) {
            if (this.ecommerce === false) {
                store.commit('setSpinner', true);
            }
            this.selectedParentCategory = null;
            this.selectedBrands = [];
            this.selectedChildCategories = [];
            this.productPage = 0;
            this.loading = true;
            await Promise.all([
                this.fetchProducts(),
                this.fetchBrands(),
                this.fetchCategories(),
            ]);
            for (let b of this.brands) {
                this.brandMap.set(b.id, b);
            }
            if (this.brandLock !== null) {
                const brand = this.brands.find(b => b.slug === this.brandLock);
                if (brand !== undefined) {
                    await this.selectBrand(brand, resetScroll, false);
                }
            }
            if (this.categoryLock !== null) {
                const category = this.categories.find(c => c.slug === this.categoryLock);
                if (category !== undefined) {
                    await this.selectParentCategory(category, resetScroll, false);
                    this.categories = [category];
                }
            }
            this.loading = false;
            if (this.ecommerce === false) {
                store.commit('setSpinner', false);
            }
        },
        resetCatalogScroll() {
            // .treade-area is always a div element
            const tradeArea = document.querySelector('.trade-area');
            const html = document.querySelector('html');
            if (html === null || tradeArea === null) {
                return;
            }
            html.scrollTop = tradeArea.offsetTop;
        },
        formatPrice(x) {
            return numWithDots(Number(x));
        },
        selectProduct(product) {
            this.$emit('product-select', product);
        },
        async fetchProducts(replace = false) {
            const query = {
                limit: 16,
                offset: this.productPage * 16,
                ecommerce: this.ecommerce,
            };
            if (this.searchString.length > 0) {
                query.name = this.searchString;
            }
            if (this.selectedChildCategories.length > 0) {
                query.categories = this.selectedChildCategories;
            }
            else if (this.selectedParentCategory !== null) {
                query.category = this.selectedParentCategory;
            }
            if (this.selectedBrands.length > 0) {
                query.brands = this.selectedBrands;
            }
            const result = await tradeAreaClient.getProducts(query);
            if (result.length < 16) {
                this.loadMoreEnabled = false;
            }
            else {
                this.loadMoreEnabled = true;
            }
            if (replace === false) {
                this.products = this.products.concat(result);
                return;
            }
            this.products = result;
        },
        async fetchBrands() {
            const query = {
                ecommerce: this.ecommerce,
            };
            if (this.selectedChildCategories.length > 0) {
                query.categories = this.selectedChildCategories;
            }
            else if (this.selectedParentCategory !== null) {
                query.category = this.selectedParentCategory;
            }
            this.brands = await tradeAreaClient.getBrands(query);
        },
        async fetchCategories() {
            const query = {
                ecommerce: this.ecommerce,
            };
            if (this.selectedBrands.length > 0) {
                query.brands = this.selectedBrands;
            }
            this.categories = await tradeAreaClient.getCategories(query);
        },
        async fetchChildCategories() {
            const query = {
                ecommerce: this.ecommerce,
            };
            if (this.selectedParentCategory !== null) {
                query.parent = this.selectedParentCategory;
            }
            if (this.selectedBrands.length > 0) {
                query.brands = this.selectedBrands;
            }
            this.childCategories = await tradeAreaClient.getCategories(query);
        },
        async search() {
            store.commit('setSpinner', true);
            this.productPage = 0;
            await this.fetchProducts(true);
            store.commit('setSpinner', false);
        },
        async selectParentCategory(category, resetScroll = true, showSpinner = true) {
            if (this.ecommerce
                && this.categoryLock !== null
                && this.selectedParentCategory !== null) {
                return;
            }
            if (this.selectedParentCategory === category.id) {
                this.selectedParentCategory = null;
            }
            else {
                this.selectedParentCategory = category.id;
            }
            if (showSpinner) {
                store.commit('setSpinner', true);
            }
            this.productPage = 0;
            await Promise.all([
                this.fetchProducts(true),
                this.fetchChildCategories(),
                this.fetchBrands(),
            ]);
            if (showSpinner) {
                store.commit('setSpinner', false);
            }
            if (resetScroll) {
                this.resetCatalogScroll();
            }
        },
        async selectChildCategory(category, resetScroll = true, showSpinner = true) {
            const index = this.selectedChildCategories.indexOf(category.id);
            if (index >= 0) {
                this.selectedChildCategories.splice(index, 1);
            }
            else {
                this.selectedChildCategories.push(category.id);
            }
            if (showSpinner) {
                store.commit('setSpinner', true);
            }
            this.productPage = 0;
            await Promise.all([
                this.fetchProducts(true),
                this.fetchBrands(),
            ]);
            if (showSpinner) {
                store.commit('setSpinner', false);
            }
            if (resetScroll) {
                this.resetCatalogScroll();
            }
        },
        async selectBrand(brand, resetScroll = true, showSpinner = true) {
            const index = this.selectedBrands.indexOf(brand.id);
            if (index >= 0) {
                this.selectedBrands.splice(index, 1);
            }
            else {
                this.selectedBrands.push(brand.id);
            }
            if (showSpinner) {
                store.commit('setSpinner', true);
            }
            this.productPage = 0;
            const promeses = [
                this.fetchProducts(true),
            ];
            if (this.ecommerce === false || this.categoryLock === null) {
                promeses.push(this.fetchCategories());
            }
            if (this.selectedParentCategory !== null) {
                promeses.push(this.fetchChildCategories());
            }
            await Promise.all(promeses);
            if (showSpinner) {
                store.commit('setSpinner', false);
            }
            if (resetScroll) {
                this.resetCatalogScroll();
            }
        },
        async loadMore() {
            this.productPage++;
            this.loadingLoadMore = true;
            await this.fetchProducts();
            this.loadingLoadMore = false;
        },
        resetFilters() {
            this.init();
        },
    },
});
